import { ROUTE_AVAILABILITY_DETAILS } from '@/constants/routes';
import HeaderTemplate from '@/templates/Header.template';
import PageSectionTemplate from '@/templates/PageSection.template';
import {
	Avatar,
	Box,
	Button,
	ButtonGroup,
	Flex,
	Heading,
	IconButton,
	Input,
	Text,
	Tooltip,
	VStack,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import { BsFillStarFill, BsStar } from 'react-icons/bs';
import { FiEdit, FiPlus, FiTrash } from 'react-icons/fi';
import { useAvailabilityList } from './availability-list.controller';

export default function AvailabilityListPage() {
	const { t, query, auth, createResource, handleDeleteClick, handleDefaultClick, history } = useAvailabilityList();

	const form = useForm({
		defaultValues: {
			query: '',
		},
	});

	if (!query.data) {
		return null;
	}

	const availabilities = query.data.resources
		.filter((resource) => {
			const query = form.watch('query');
			if (!query) {
				return true;
			}
			return resource.name.toLocaleLowerCase().includes(query);
		})
		.sort((a, b) => a.name.localeCompare(b.name));

	const hasNoAvailabilities = availabilities.length === 0;

	return (
		<PageSectionTemplate>
			<HeaderTemplate
				left={<></>}
				center={t('Availability')}
				right={
					<Button colorScheme="primary" size="sm" onClick={createResource} rightIcon={<FiPlus />}>
						{t('Create')}
					</Button>
				}
			/>

			{hasNoAvailabilities ? (
				<Box p={4}>
					<Heading size="md">{t(`Availabilities`)}</Heading>
					<Text my="4" color="gray.600">
						{t(
							'Create availabilities to configure when you want to be available for meetings. Your availabilities will be visible on your teams so you can create group meetings. You can reuse your availabilities in multiple reservation calendars.'
						)}
					</Text>
				</Box>
			) : (
				<VStack overflow="auto" pb={4} w="full" gap={2}>
					<Box w="full" mb={4}>
						<Input variant="filled" type="search" {...form.register('query')} placeholder={t('Search')} />
					</Box>
					{availabilities?.map((resource) => (
						<Box
							w="full"
							alignItems="center"
							bg="white"
							borderColor="gray.200"
							borderRadius="md"
							borderWidth={1}
							display="grid"
							gridTemplateColumns="1fr auto"
							key={resource.id}
							px={6}
							py={4}
						>
							<Flex flexDir="row" gap={4}>
								<Tooltip label={resource.owner.email}>
									<Avatar size="xs" name={resource.owner.email}></Avatar>
								</Tooltip>
								<Text fontWeight={500}>{resource.name}</Text>
							</Flex>

							<Box display="flex" justifyContent="flex-end">
								<ButtonGroup isAttached variant="outline">
									{resource.tags?.tt_default === 'true' ? (
										<Tooltip label={t('This is your default availability')}>
											<IconButton aria-label={t('Default')} icon={<BsFillStarFill />} />
										</Tooltip>
									) : (
										<Tooltip label={t('Use this availability in all new services.')}>
											<IconButton
												onClick={() => handleDefaultClick(resource.id)}
												aria-label={t('Default')}
												icon={<BsStar />}
											/>
										</Tooltip>
									)}
									<Tooltip label={t('Delete')}>
										<IconButton
											isDisabled={resource.owner.id !== auth.getCurrentUser().userId}
											onClick={() => {
												handleDeleteClick(resource.id);
											}}
											aria-label={t('Delete')}
											icon={<FiTrash />}
										/>
									</Tooltip>

									<Tooltip label={t('Edit')}>
										<IconButton
											onClick={() => {
												history.push(ROUTE_AVAILABILITY_DETAILS.replace(':id', resource.id));
											}}
											aria-label={t('Edit')}
											icon={<FiEdit />}
										/>
									</Tooltip>
								</ButtonGroup>
							</Box>
						</Box>
					))}
				</VStack>
			)}
		</PageSectionTemplate>
	);
}
